<template>
    <div class="grey lighten-5">
        <div id="ai_container" class="d-flex">
            <v-navigation-drawer 
                v-model="sidebarVisible"
                app
                :width="isMobilePort ? '70%' : 280">
                <ai-navigator-sidebar
                    :groupedDates="groupedDates"
                    @loadConversations="loadConversations"
                    class="fill-height py-4"
                    id="ai_right">
                </ai-navigator-sidebar>
            </v-navigation-drawer>

            <div id="main_content content-container" class="flex-grow-1 grey lighten-5" :class="isMobileAndTablet? 'pa-3' : ''">
                <div class="d-flex flex-column h-full" style="max-width: 960px; margin: 0 auto;">
                    <div id="msg_rows" class="flex-grow-1 overflow-auto pr-1 pr-lg-3">

                        <ai-navigator-start
                            v-if="isNewChatMode"
                            @question-clicked="onQuestionClicked"
                            :can-send-messages="!isQuestionLimitReached">
                        </ai-navigator-start>

                        <!--- here go past questions and answers --->
                        <div
                            v-else
                            v-for="(conversation, index) in visibleConversations"
                            :key="index"
                            class="msg_item"
                        >

                            <divider-with-text :text="formatContextForDividerText(conversation)"></divider-with-text>
                            <chat-message type="user" class="mb-3">
                                {{ conversation.question }}
                            </chat-message>
                            <!--- is this a question that was JUST asked? we render those gradually --->
                            <chat-server-message v-if="conversation.live" :prompt="conversation" class="mb-5">
                            </chat-server-message>
                            <chat-message v-else-if="conversation.response" type="system" class="mb-3">
                                <chat-question-response :response="conversation.response"></chat-question-response>
                            </chat-message>

                        </div>
                    </div>

                    <div id="input_box" class="mt-3 mt-lg-5">
                        <question-limit-alert
                            :countdownremaining="countdownremaining"
                            :countdown-reset="countdownReset">
                        </question-limit-alert>

                        <div class="d-flex justify-space-between align-center mb-1 text-muted">
                            <div>
                                <v-progress-linear
                                    v-if="codeCyclesLoading"
                                    :active="true"
                                    color="primary"
                                    indeterminate
                                    style="min-width: 10em;">
                                </v-progress-linear>

                                <p v-else-if="codeCyclesError" class="red--text mb-0">
                                    {{ codeCyclesError }}
                                </p>

                                <div v-else-if="selectedLocation && selectedYear" class="d-flex align-center text-sm pa-1">
                                    <span class="text-no-wrap">
                                        {{ selectedLocation }} &#8226; {{ selectedYear }}
                                    </span>

                                    <v-btn
                                        v-if="isMobileAndTablet"
                                        text
                                        small
                                        class="ml-2"
                                        @click="sidebarVisible = true"
                                    >
                                        <v-icon>mdi-tune</v-icon>
                                    </v-btn>
                                </div>

                            </div>
                        </div>

                        <v-textarea
                            label="Enter question"
                            v-model="question"
                            hide-details outlined
                            rows="1"
                            auto-grow
                            counter="true"
                            autocomplete="off"
                            @keydown.enter="onEnterClicked"
                            class="white">
                            <template v-slot:append>
                                <v-progress-circular v-if="isBusy" indeterminate color="grey" size="28"
                                    class="disabled-icon"></v-progress-circular>

                                <v-btn v-else icon small color="primary" @click="sendMessage"
                                    aria-label="Send question">
                                    <v-icon>mdi-send-outline</v-icon>
                                </v-btn>
                            </template>
                        </v-textarea>

                        <div class="d-flex justify-space-between mt-2">
                            <p>&nbsp;</p>
                            <p class="caption text--secondary">{{ currentCharCount }} / {{ maxCharCount }} chars</p>
                        </div>
                    </div>
                </div>

            </div>
        </div>
        <confirm-terms-dialog 
            :visible="disclaimerModalVisible"
            :can-be-closed="!disclaimerConfirmationRequired"
            @closed="disclaimerModalVisible = false"
            @confirmed="disclaimerConfirmed">
        </confirm-terms-dialog>
        <notes></notes>
    </div>
</template>

<script>
import AiNavigatorSidebar from "./AiNavigatorSidebar.vue";
import TextTyped from "../TextTyped.vue";
import AssetImage from "../AssetImage.vue";
import ChatServerMessage from "./chat/ChatServerMessage.vue";
import AiNavigatorStart from "./AiNavigatorStart.vue";
import ChatMessage from "./chat/ChatMessage.vue";
import { ApiService } from "../../api/ApiService";
import { User } from '../../classes/User';
import ConfirmTermsDialog from "./ConfirmTermsDialog.vue";
import NavigatorTipsBlock from "./blocks/NavigatorTipsBlock.vue";
import NavigatorSidebarDisclaimerBlock from "./blocks/NavigatorSidebarDisclaimerBlock.vue";
import Notes from '../content-section/Dialogs/index.vue';
import QuestionLimitAlert from "./QuestionLimitAlert.vue";
import { EasyStorage } from "../../classes/EasyStorage";
import { StringUtil } from "../../util/StringUtil";
import Mobile from "../inc/mixins/mobile";
import NavigatorLocationSelection from "./blocks/NavigatorLocationSelection.vue";
import ShareFeedback from "../links/ShareFeedback";
import MarkdownText from "../MarkdownText.vue";
import ChatQuestionResponse from "./chat/ChatQuestionResponse.vue";
import DividerWithText from "../DividerWithText.vue";
import { DateUtil } from "../../util/DateUtil";
import { BrowserUtils } from "../../util/BrowserUtils";

export default {
    name: 'AiNavigator',
    mixins: [
        Mobile
    ],
    components: {
        DividerWithText,
        ChatQuestionResponse,
        MarkdownText,
        NavigatorLocationSelection,
        QuestionLimitAlert,
        NavigatorSidebarDisclaimerBlock,
        NavigatorTipsBlock,
        ConfirmTermsDialog,
        Notes,
        ChatMessage,
        AiNavigatorStart,
        ChatServerMessage,
        AssetImage,
        TextTyped,
        AiNavigatorSidebar,
        ShareFeedback
    },
    data() {
        return {
            locationSelectionModal: false,
            disclaimerConfirmationRequired: false,
            disclaimerModalVisible: false,
            mobileHeaderDropdownVisible: false,
            maxCharCount: 500,
            isBusy: false,
            question: '',
            // code cycle stuff
            codeCyclesLoading: false,
            codeCyclesError: '',
            countdownremaining: null,
            countdownReset: null,
            codeCyclesAll: [],
            questionId: null,
            sidebarVisible: true,
            conversations: [],
            serverRequest: null, // can only have one server request at a time
            groupedDates: []
        }
    },
    computed: {
        user() {
            return this.$store.getters.getUser;
        },
        currentCharCount() {
            return this.question.length;
        },
        isQuestionValidLength() {
            return this.question.length <= this.maxCharCount;
        },
        isQuestionLimitReached() {
            return this.countdownremaining === 0;
        },
        canSendMessage() {

            if (this.isQuestionLimitReached) {
                return false;
            }

            return this.question.length > 0 && this.isQuestionValidLength && this.isBusy === false;
        },
        selectedLocation() {
            return this.$store.getters.getCodeCycleLocation;
        },
        selectedYear() {
            return this.$store.getters.getCodeCycleYear;
        },
        visibleConversations() {
            return this.conversations || [];
        },
        currentQuestionDate() {
            return this.$store.getters.getAiNavigatorCurrentQuestionDate;
        },
        isNewChatMode() {
            return !(this.currentQuestionDate);
        },
        todaysDate() {
            return DateUtil.todaysDate();
        }
    },
    methods: {
        formatContextForDividerText(conversation) {
            // Display divider between each question reiterates the date (i.e. "Jul 15") and parameters used for each question (i.e. "2024 I-Codes")
            const md = DateUtil.formatDateMMMDY(conversation.created_at);
            const year = conversation?.scope?.year || "";
            const location = conversation?.scope?.location || "";

            return [md, location, year].filter(Boolean).join(' &bull; ');
        },
        loadConversations(questionDate) {
            ApiService.getAiNavigatorConversations(questionDate).then((conversations) => {
                this.conversations = conversations?.conversations || [];
            });
        },
        async loadGroupedDates() {
            await ApiService.getAiNavigatorDateGroups().then((groupedDates) => {
                this.groupedDates = groupedDates?.groupedDates || [];
            });
        },
        
        disclaimerConfirmed() {
            this.disclaimerConfirmationRequired = false;
            this.disclaimerModalVisible = false;
        },
        validateQuestion(question) {
            // TODO: return false is sensitive information found
            return true;
        },
        onQuestionClicked(question) {

            if (this.countdownremaining === 0) {
                return false;
            }

            //in case of recent asked questions available it provides as object otherwise a simple string
            if (typeof question === 'object') {
                const { question: text, scope, questionId } = question;
                this.question = text;
                if (scope && scope.cycles && scope.cycles.length > 0) {
                    this.$store.commit('setCodeCycle', scope.cycles[0]);
                    this.$store.commit('setCodeCycleLocation', scope.location);
                    this.$store.commit('setCodeCycleYear', scope.year);
                    ApiService.updatePreference({
                        [User.CODE_CYCLE_LOCATION]: scope.location,
                        [User.CODE_CYCLE_YEAR]: scope.year,
                    });
                }
                this.questionId = questionId;
            } else {
                this.question = question;
            }
            this.sendMessage();
        },
        onEnterClicked(event) {

            /** @type {KeyboardEvent} */
            const ev = event;

            // SHIFT + Enter = creates new line rather than SEND message
            if (ev.shiftKey) {
                return false;
            }

            ev.preventDefault();

            if (this.canSendMessage) {
                this.sendMessage();
            } else {
                return false;
            }
        },
        sendMessage() {

            if (!this.canSendMessage) {
                return;
            }

            if (!this.validateQuestion(this.question)) {
                return;
            }

            // once server request comes back, this will change to false
            this.isBusy = true;

            this.conversations.push({
                created_at: new Date().toISOString(),
                question: this.question,
                live: true,
                response: null,
                scope: {
                    location: this.$store.getters.getCodeCycleLocation,
                    year: this.$store.getters.getCodeCycleYear
                }
            });

            // send that question to our AI server!
            const location = this.$store.getters.getCodeCycleLocation || "";
            const year = this.$store.getters.getCodeCycleYear || "";
            const bookIds = this.$store.getters.getCodeCycleBookIds || [];

            const request = ApiService.postQuestionGPT(this.question, location, year, bookIds, this.questionId);
            // const request = ApiService.postQuestionFakeGPT(this.question, false);

            request.promise.then((response) => {
                this.updateResponseReceived(response);
                
                if (!this.currentQuestionDate || this.todaysDate !== this.currentQuestionDate) {
                    this.loadGroupedDates();
                    this.loadConversations(this.todaysDate);
                    this.$store.commit('setAiNavigatorCurrentQuestionDate',this.todaysDate);
                }
                
                // and how many questions do we have left?
                const data = {
                    remaining: response.remaining,
                    reset: response.reset
                };
                this.onCountDown(data);
            }).catch((err) => {
                const msg = err?.message || "";

                // This exists only if the request reached the server and got a response
                const response = err?.response;

                let error = null;

                // was it cancelled??
                if (msg.includes("Network Error")) {
                    error = "Uh-oh! We're experiencing some network issues right now. Please check your connection and try again.";
                } else if (response && response.status === 504) {
                    // PRA-9441
                    error = "Server is taking too long to respond. Please try again later.";
                } else {
                    error = err?.response?.data?.error || err;
                }

                this.updateResponseReceived({
                    error: error
                });

            }).finally(() => {
                this.isBusy = false;
            });

            this.serverRequest = request;

            setTimeout(() => {
                this.question = '';
                this.questionId = null;
            }, 50);
        },
        updateResponseReceived(response) {

            // which entry to update? the one where response is null
            const pendingIndex = this.conversations.findIndex((item) => item.response === null);

            if (pendingIndex !== -1) {

                const newObject = {
                    ...this.conversations[pendingIndex],
                    response: response
                };

                // EXPLICITLY reactive
                this.$set(this.conversations, pendingIndex, newObject);
            }
        },
        onCountDown(data) {
            // will be stored as String first
            localStorage.setItem('ain_remaining', data.remaining);

            this.countdownremaining = data.remaining;
            this.countdownReset = data.reset;
        },
        getLimits() {

            ApiService.getLimits()
                .then((data) => {
                    this.countdownReset = data.reset;
                    this.countdownremaining = data.remaining;
                });
        },
        resetUrlQueryString() {
            window.history.pushState('', 'AI Navigator', this.getRoute('ai_navigator'));
        },
        setAiContainerHeight() {
            const systemBar = document.getElementById('app-system-bar');
            let navigationHeader = document.getElementById('navigation-app');
            const aiHeader = document.getElementById('ai_header');
            const aiContainer = document.getElementById('ai_container');

            if (!navigationHeader) {
                navigationHeader = document.getElementById('navigation-app-mobile')
            }

            let offsetHeight = navigationHeader.offsetHeight; // + aiHeader.offsetHeight;
            if (systemBar) {
                offsetHeight += systemBar.offsetHeight;
            }

            aiContainer.style.setProperty('height', `calc(100vh - ${offsetHeight}px)`, 'important');
        }
    },
    created() {

        this.getLimits();

        this.$watch(() => this.$store.getters.getUser, (user) => {

            this.loadGroupedDates();
            if (user) {
                this.disclaimerConfirmationRequired = !!user.ai_navigator_terms_confirmation_required;

                if (user.ai_navigator_terms_confirmation_required) {
                    this.disclaimerModalVisible = true;
                }
            }

        }, { immediate: true })

        const urlParams = new URLSearchParams(window.location.search);
        const questionAsked = urlParams.get('ask');

        if (questionAsked && '' !== questionAsked) {
            this.question = questionAsked;
            this.resetUrlQueryString();
        }
        if (this.isMobileAndTablet) {
            this.sidebarVisible = false;
        }
    },
    mounted() {

        EventBus.listen('ai_navigator_show_disclaimer', () => {
            this.disclaimerModalVisible = true;
        });

        Mousetrap.bind('left+left+left+c', () => {
            this.disclaimerModalVisible = false;
        });

        /** @type {HTMLDivElement} */
        const el = document.querySelector('#main-content-container > div.body.overflow-hidden');

        if (el) {
            el.style.setProperty('min-height', '1px', 'important');
        }

        this.$store.commit('setFooterVisible', false);
        this.$store.commit('setMobileActionsVisible', false);

        // when switching between dates (or new convo added to current) - auto scroll to the bottom  just like ChatGPT
        this.$watch(() => [this.$store.getters.getAiNavigatorCurrentQuestionDate, this.conversations], () => {

            this.$nextTick(() => {
                BrowserUtils.scrollElementToBottomSmoothly('#msg_rows');
            });
        });

        EasyStorage.watch("ain_remaining", (newValue) => {

            if (StringUtil.isNumeric(newValue)) {
                this.countdownremaining = parseInt(newValue);
            }

        });

        this.setAiContainerHeight();
        window.addEventListener('resize', this.setAiContainerHeight);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.setAiContainerHeight);
    }
}
</script>

<style lang="scss" scoped>
#question_block {
    min-height: 100vh;
    position: relative;
}
</style>

<style lang="scss">
.disabled-icon {
    cursor: not-allowed !important;
}

.border-bottom {
    border-bottom: 1px solid lightgray;
}

.msg_item:last-child {
    border-bottom: rgba(0, 0, 0, .12) solid 1px;
    margin-bottom: 24px;
    padding-bottom: 16px;
}

@media (min-width: 1264px) {
    .lg\:w-9\/12 {
        width: 75%
    }
}
</style>
